import { type Ref, useCallback, useImperativeHandle, useRef, useState } from 'react';

import {
  ButtonEditorIconGrid,
  ButtonEditorIconPlacement,
  ButtonEditorSettingToggle,
} from '@/components/shared/button-editor';
import { LinkSettingsFields } from '@/components/shared/link-settings-fields';
import { SelectInput, TextInput } from '@/components/ui/form';
import { isPageLinkType } from '@/lib/blocks/button-links';
import type {
  ButtonConfig,
  ButtonIconName,
  ButtonIconPlacement,
  ButtonStyle,
} from '@/types/button';

export const BUTTON_TYPE_OPTIONS: Array<{
  value: ButtonStyle;
  title: string;
  description?: string;
  disabled?: boolean;
}> = [
  { value: 'primary', title: 'Primary' },
  { value: 'secondary', title: 'Secondary' },
];

type UpdateDraftFn = (
  nextDraftOrUpdater: ButtonConfig | ((current: ButtonConfig) => ButtonConfig),
) => void;

export interface ButtonEditorFormHandle {
  getValue: () => ButtonConfig;
  hasValidDestination: () => boolean;
}

interface ButtonEditorFormProps {
  initialValue: ButtonConfig;
  resetKey?: unknown;
  enableSearch: boolean;
  onChange?: (value: ButtonConfig) => void;
  ref?: Ref<ButtonEditorFormHandle | null>;
}

function ButtonTypeField({
  draft,
  updateDraft,
}: {
  draft: ButtonConfig;
  updateDraft: UpdateDraftFn;
}) {
  return (
    <SelectInput
      label="Button Type"
      value={draft.style || 'primary'}
      onValueChange={(value) =>
        updateDraft((current) => ({
          ...current,
          style: value as ButtonStyle,
        }))
      }
      searchable={false}
      contentClassName="z-[150]"
      options={BUTTON_TYPE_OPTIONS}
    />
  );
}

function IconPicker({
  draft,
  hasIcon,
  updateDraft,
}: {
  draft: ButtonConfig;
  hasIcon: boolean;
  updateDraft: UpdateDraftFn;
}) {
  return (
    <div className="space-y-4">
      <ButtonEditorSettingToggle
        title="Icon"
        description="Include an icon in the button"
        checked={hasIcon}
        onCheckedChange={(checked) => {
          updateDraft((current) => ({
            ...current,
            icon: checked ? current.icon || 'phone' : undefined,
          }));
        }}
      />

      {hasIcon ? (
        <ButtonEditorIconGrid
          value={draft.icon}
          onValueChange={(icon) =>
            updateDraft((current) => ({
              ...current,
              icon: icon as ButtonIconName,
            }))
          }
        />
      ) : null}
    </div>
  );
}

function IconPlacementField({
  draft,
  updateDraft,
}: {
  draft: ButtonConfig;
  updateDraft: UpdateDraftFn;
}) {
  if (!draft.icon) {
    return null;
  }

  return (
    <ButtonEditorIconPlacement
      value={draft.iconPlacement || 'before'}
      onValueChange={(value) =>
        updateDraft((current) => ({
          ...current,
          iconPlacement: value as ButtonIconPlacement,
        }))
      }
    />
  );
}

export function ButtonEditorForm({
  initialValue,
  resetKey,
  enableSearch,
  onChange,
  ref,
}: ButtonEditorFormProps) {
  const [draft, setDraft] = useState<ButtonConfig>(initialValue);
  const prevResetKeyRef = useRef(resetKey);
  if (resetKey !== prevResetKeyRef.current) {
    setDraft(initialValue);
    prevResetKeyRef.current = resetKey;
  }

  const linkType = draft.linkType || 'external';
  const hasIcon = Boolean(draft.icon);

  const updateDraft = useCallback(
    (nextDraftOrUpdater: ButtonConfig | ((current: ButtonConfig) => ButtonConfig)) => {
      setDraft((current) => {
        const nextDraft =
          typeof nextDraftOrUpdater === 'function'
            ? nextDraftOrUpdater(current)
            : nextDraftOrUpdater;
        onChange?.(nextDraft);
        return nextDraft;
      });
    },
    [onChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      getValue: () => draft,
      hasValidDestination: () =>
        isPageLinkType(linkType) ? Boolean(draft.internalPageId) : Boolean(draft.link?.trim()),
    }),
    [draft, linkType],
  );

  return (
    <>
      <TextInput
        label="Button Text"
        value={draft.text}
        onChange={(event) => updateDraft((current) => ({ ...current, text: event.target.value }))}
        placeholder="Button text"
      />

      <ButtonTypeField draft={draft} updateDraft={updateDraft} />
      <IconPicker draft={draft} hasIcon={hasIcon} updateDraft={updateDraft} />
      {hasIcon ? <IconPlacementField draft={draft} updateDraft={updateDraft} /> : null}
      <LinkSettingsFields
        value={{
          linkType,
          link: draft.link,
          internalPageId: draft.internalPageId,
          openInNewTab: draft.openInNewTab,
          nofollow: draft.nofollow,
        }}
        enableSearch={enableSearch}
        onChange={(nextValue) => updateDraft((current) => ({ ...current, ...nextValue }))}
      />
    </>
  );
}
